import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Megaphone, Plus, X } from 'lucide-react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const HRAnnouncements = () => {
  const { getAuthHeader } = useAuth();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'normal',
    target_audience: 'all'
  });

  useEffect(() => {
    fetchAnnouncements();
  }, []);

  const fetchAnnouncements = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/hr/announcements`, {
        headers: getAuthHeader()
      });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Error fetching announcements:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(
        `${BACKEND_URL}/api/hr/announcements`,
        formData,
        { headers: getAuthHeader() }
      );
      alert('Announcement created successfully!');
      setShowForm(false);
      setFormData({ title: '', content: '', priority: 'normal', target_audience: 'all' });
      fetchAnnouncements();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to create announcement');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to deactivate this announcement?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/hr/announcements/${id}`, {
        headers: getAuthHeader()
      });
      alert('Announcement deactivated');
      fetchAnnouncements();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete announcement');
    }
  };

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800',
      high: 'bg-orange-100 text-orange-800',
      normal: 'bg-blue-100 text-blue-800',
      low: 'bg-gray-100 text-gray-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (loading) return <div>Loading announcements...</div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Megaphone size={28} />
            Announcements
          </h2>
          <p className="text-gray-600 mt-1">Manage company-wide announcements</p>
        </div>
        <Button onClick={() => setShowForm(!showForm)}>
          {showForm ? <X size={18} className="mr-2" /> : <Plus size={18} className="mr-2" />}
          {showForm ? 'Cancel' : 'New Announcement'}
        </Button>
      </div>

      {/* Create Form */}
      {showForm && (
        <Card className="border-primary">
          <CardHeader className="bg-primary/5">
            <CardTitle>Create New Announcement</CardTitle>
          </CardHeader>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Title *</label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full px-3 py-2 border rounded-md"
                  placeholder="Announcement title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Content *</label>
                <Textarea
                  required
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  rows={4}
                  placeholder="Announcement details..."
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Priority</label>
                  <select
                    value={formData.priority}
                    onChange={(e) => setFormData({...formData, priority: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="low">Low</option>
                    <option value="normal">Normal</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Target Audience</label>
                  <select
                    value={formData.target_audience}
                    onChange={(e) => setFormData({...formData, target_audience: e.target.value})}
                    className="w-full px-3 py-2 border rounded-md"
                  >
                    <option value="all">All Employees</option>
                    <option value="department">Specific Department</option>
                    <option value="specific">Specific Employees</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3">
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  Create Announcement
                </Button>
                <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Active Announcements */}
      <Card>
        <CardHeader>
          <CardTitle>Active Announcements ({announcements.filter(a => a.is_active).length})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {announcements.filter(a => a.is_active).map((announcement) => (
              <div key={announcement.id} className="p-4 border rounded-lg">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-bold text-lg">{announcement.title}</h4>
                      <Badge className={getPriorityColor(announcement.priority)}>
                        {announcement.priority.toUpperCase()}
                      </Badge>
                    </div>
                    <p className="text-gray-700 whitespace-pre-wrap">{announcement.content}</p>
                    <div className="flex items-center gap-3 mt-3 text-sm text-gray-600">
                      <span>By {announcement.created_by}</span>
                      <span>•</span>
                      <span>{new Date(announcement.created_at).toLocaleDateString()}</span>
                      <span>•</span>
                      <span>Target: {announcement.target_audience}</span>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleDelete(announcement.id)}
                    className="ml-4 text-red-600 hover:bg-red-50"
                  >
                    <X size={16} />
                  </Button>
                </div>
              </div>
            ))}
            {announcements.filter(a => a.is_active).length === 0 && (
              <p className="text-center py-8 text-gray-500">No active announcements</p>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Inactive Announcements */}
      {announcements.filter(a => !a.is_active).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-gray-600">Inactive Announcements</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {announcements.filter(a => !a.is_active).map((announcement) => (
                <div key={announcement.id} className="p-3 border rounded-lg bg-gray-50 opacity-60">
                  <div className="font-medium">{announcement.title}</div>
                  <div className="text-sm text-gray-600">
                    Deactivated • {new Date(announcement.created_at).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HRAnnouncements;